import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { MoreHorizontal } from "lucide-react";
import clsx from "clsx";

export interface FileRowMenuItem {
  key: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  // Shown under a disabled item, so the reason is readable without hovering.
  hint?: string;
}

type Props = {
  items: FileRowMenuItem[];
  ariaLabel: string;
};

const MENU_WIDTH = 260;

function MenuItemButton({
  item,
  onPicked,
  className,
}: {
  item: FileRowMenuItem;
  onPicked: () => void;
  className: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={item.disabled}
      className={clsx(
        "block w-full text-left text-zinc-800 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:text-zinc-400 disabled:hover:bg-transparent",
        className
      )}
      onClick={() => {
        if (item.disabled) return;
        onPicked();
        item.onClick();
      }}
    >
      {item.label}
      {item.hint && (
        <span className="mt-0.5 block text-xs font-normal text-zinc-500">{item.hint}</span>
      )}
    </button>
  );
}

/**
 * Per-row "more actions" menu of the files list (desktop / tablet): a lucide
 * trigger and a menu portalled to <body>, so it is never clipped by a
 * scrolling card around the list.
 */
export default function FileRowMenu({ items, ariaLabel }: Props) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Latest placement function, so the layout effect below can re-run it once
  // the menu is actually in the DOM and its height can be measured.
  const placeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      let left = r.right - MENU_WIDTH;
      left = Math.min(Math.max(8, left), window.innerWidth - MENU_WIDTH - 8);
      // Before the menu is mounted its height is unknown; assume the full
      // rendered height so a row near the bottom does not open off-screen,
      // then place() runs again from the layout effect below with the real
      // measurement.
      const menuHeight = (menuRef.current?.offsetHeight || items.length * 36 + 16) + 8;
      let top = r.bottom + 4;
      if (top + menuHeight > window.innerHeight - 8) {
        top = Math.max(8, r.top - menuHeight);
      }
      setCoords({ top, left });
    };
    place();
    placeRef.current = place;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("mousedown", onDoc);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open, items.length]);

  // Re-place once the menu is actually in the DOM: the first run happens
  // before the portal exists, so its height there is only an estimate.
  useLayoutEffect(() => {
    if (open && coords && menuRef.current) placeRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, coords === null]);

  if (!items.length) {
    return null;
  }

  return (
    <div className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              zIndex: 80,
              width: MENU_WIDTH,
            }}
            className="overflow-hidden rounded-lg border border-zinc-200 bg-white py-1 shadow-lg"
          >
            {items.map((item) => (
              <MenuItemButton
                key={item.key}
                item={item}
                onPicked={() => setOpen(false)}
                className="px-3 py-2 text-sm"
              />
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

/** The same menu on a phone: a footer button opening a sheet from the bottom. */
export function FileRowSheet({ items, ariaLabel }: Props) {
  const [open, setOpen] = useState(false);

  if (!items.length) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-10 shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-white py-2 text-zinc-600"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} className="relative z-[1000]">
        <DialogBackdrop className="fixed inset-0 bg-zinc-950/40" />
        <div className="fixed inset-x-0 bottom-0">
          <DialogPanel className="rounded-t-2xl bg-white px-3 pb-4 pt-2 shadow-xl">
            <DialogTitle className="sr-only">{ariaLabel}</DialogTitle>
            <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-zinc-200" aria-hidden="true" />
            <div role="menu" aria-label={ariaLabel} className="divide-y divide-zinc-100">
              {items.map((item) => (
                <MenuItemButton
                  key={item.key}
                  item={item}
                  onPicked={() => setOpen(false)}
                  className="px-2 py-3 text-base"
                />
              ))}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}
